/*
  Form item component for use in form creator

  Supports the following input types:
  - Date range picker
  - Autocomplete
  - Checkbox
  - Control list
  - Date picker
  - Time picker
  - Date and Time picker
  - Radio
  - Select List
  - Text Input
*/

import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import FormCheckbox from './FormCheckbox'
import FormDateRangePicker from './FormDateRangePicker'
import FormRadio from './FormRadio'
import FormCheckboxList from './FormCheckboxList'
import FormTextField from './FormTextField'

import FormControlList from './FormControlList'
import FormDatePicker from './FormDatePicker'
import FormSelect from './FormSelect'
import FormTimePicker from './FormTimePicker'
import less from './index.less'
import FormAutocomplete from './FormAutocomplete'
import Typography from '@mui/material/Typography'

const FormItem = ({
  id,
  label,
  name,
  component,
  type = 'text',
  inputConfig = {},
  dateprops = {},
  timeprops = {},
  defaultValue,
  errors,
  control,
  placeholder,
  disabled,
  tooltip,
}) => {

  if (type === 'labelOnly') {
    const color =
      inputConfig?.inputBaseProps?.labelProps?.sx?.color || '#2D2D6C'

    return (
      <div className={less.controllerContent} style={{ marginTop: 6, marginBottom: -5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color }}>
          {label}
        </Typography>
      </div>
    )
  }

  let formComponent = component

  if (!component) {
    switch (type) {
      case 'text':
        formComponent = componentProps => <FormTextField {...componentProps} hideDefaultSearch />
        break
      case 'password':
        formComponent = componentProps => <FormTextField {...componentProps} type="password" />
        break
      case 'select':
        formComponent = FormSelect
        break
      case 'autocomplete':
        formComponent = FormAutocomplete
        break
      case 'date':
        formComponent = FormDatePicker
        break
      case 'time':
        formComponent = FormTimePicker
        break
      case 'dateRange':
        formComponent = FormDateRangePicker
        break
      case 'radio':
        formComponent = FormRadio
        break
      case 'checkbox':
        formComponent = FormCheckbox
        break
      case 'checkboxList':
        formComponent = FormCheckboxList
        break
      case 'controlList':
        formComponent = FormControlList
        break
      default:
        break
    }
  }

  return (
    <Controller
      defaultValue={defaultValue}
      name={name}
      control={control}
      render={formProps => {
        const passProps = { ...formProps }
        const error = errors?.[name]

        passProps.fieldState.error = error
        const propsOnChange = passProps.field.onChange

        passProps.field.onChange = val => {
          propsOnChange?.(val)
          inputConfig.onChange?.(val)
        }

        return (
          <div className={less.controllerContent}>
            {formComponent({
              formProps: passProps,
              inputConfig,
              itemProps: { id, label, type, placeholder, disabled, dateprops, timeprops, tooltip },
            })}
          </div>
        )
      }}
    />
  )
}

FormItem.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  component: PropTypes.func,
  inputConfig: PropTypes.object,
  itemConfig: PropTypes.object,
  dateprops: PropTypes.object,
  timeprops: PropTypes.object,
  type: PropTypes.string,
  defaultValue: PropTypes.any,
  control: PropTypes.any,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
  tooltip: PropTypes.string,
}

export default FormItem
